export const PORTAL_SYNC_STATUSES = [
  '',
  'pending',
  'queued',
  'submitted',
  'accepted',
  'processing',
  'published',
  'rejected',
  'unpublish_pending',
  'unpublished',
  'unknown',
  'failed',
];

export const PORTAL_OPERATIONAL_STATES = [
  '',
  'processing',
  'published',
  'rejected',
  'unknown',
  'failed',
  'unpublished',
];

export const TERMINAL_PORTAL_SYNC_STATUSES = [
  'published',
  'rejected',
  'unpublished',
  'failed',
];

const STATE_TONES = {
  pending: 'warning',
  queued: 'warning',
  submitted: 'info',
  accepted: 'info',
  processing: 'info',
  unpublish_pending: 'warning',
  published: 'positive',
  rejected: 'danger',
  failed: 'danger',
  unknown: 'warning',
  unpublished: 'neutral',
};

export const portalSyncTone = state => STATE_TONES[state] || 'neutral';

export const normalizeAttempt = attempt => ({
  id: attempt?.id || '',
  action: attempt?.action || '',
  status: attempt?.status || 'unknown',
  attemptNumber: Number(attempt?.attempt_number || attempt?.attemptNumber || 0),
  remoteStatus: attempt?.remote_status || attempt?.remoteStatus || '',
  remoteVersion: attempt?.remote_version || attempt?.remoteVersion || '',
  errorClass: attempt?.error_class || attempt?.errorClass || '',
  errorCode: attempt?.error_code || attempt?.errorCode || '',
  startedAt: attempt?.started_at || attempt?.startedAt || '',
  completedAt: attempt?.completed_at || attempt?.completedAt || '',
  metadata: attempt?.metadata || {},
});

export const normalizePortalAction = action => ({
  key: action?.key || '',
  enabled: Boolean(action?.enabled),
  readOnly: Boolean(action?.read_only || action?.readOnly),
  disabledReason: action?.disabled_reason || action?.disabledReason || '',
  idempotencyRequired: Boolean(
    action?.idempotency_required || action?.idempotencyRequired
  ),
});

export const normalizeFailure = failure => ({
  category: failure?.category || 'none',
  code: failure?.code || '',
  message: failure?.message || '',
  retryable: Boolean(failure?.retryable),
  action: failure?.action || '',
  source: failure?.source || 'none',
  redacted: failure?.redacted !== false,
});

export const normalizeDivergence = divergence => ({
  present: Boolean(divergence?.present),
  desiredState: divergence?.desired_state || divergence?.desiredState || '',
  observedState: divergence?.observed_state || divergence?.observedState || '',
  localVersion: divergence?.local_version || divergence?.localVersion || '',
  remoteVersion: divergence?.remote_version || divergence?.remoteVersion || '',
  lastCheckedAt: divergence?.last_checked_at || divergence?.lastCheckedAt || '',
});

export const normalizeReadiness = readiness => ({
  status: readiness?.status || 'unknown',
  providerStatus: readiness?.provider_status || readiness?.providerStatus || '',
  syncStatus: readiness?.sync_status || readiness?.syncStatus || '',
  lastSyncAt: readiness?.last_sync_at || readiness?.lastSyncAt || '',
  lastStatusCheckAt:
    readiness?.last_status_check_at || readiness?.lastStatusCheckAt || '',
  nextStatusCheckAt:
    readiness?.next_status_check_at || readiness?.nextStatusCheckAt || '',
  readOnly: Boolean(readiness?.read_only || readiness?.readOnly),
  failure: normalizeFailure(readiness?.failure),
  divergence: normalizeDivergence(readiness?.divergence),
  redaction: readiness?.redaction || {},
});

export const normalizeSync = sync => {
  const attemptCounts = sync?.attempt_counts || sync?.attemptCounts || {};
  const attemptHistory = Array.isArray(sync?.attempt_history)
    ? sync.attempt_history.map(normalizeAttempt)
    : [];
  const actions = Array.isArray(sync?.actions)
    ? sync.actions.map(normalizePortalAction)
    : [];

  return {
    id: sync?.id || '',
    imovelId: sync?.imovel_id || sync?.imovelId || '',
    imovelListingId: sync?.imovel_listing_id || sync?.imovelListingId || '',
    portal: sync?.portal || '',
    status: sync?.status || 'unknown',
    operationalState:
      sync?.operational_state ||
      sync?.operationalState ||
      sync?.status ||
      'unknown',
    desiredState: sync?.desired_state || sync?.desiredState || '',
    observedState: sync?.observed_state || sync?.observedState || '',
    remoteId: sync?.remote_id || sync?.remoteId || '',
    remoteVersion: sync?.remote_version || sync?.remoteVersion || '',
    externalRef: sync?.external_ref || sync?.externalRef || '',
    lastAction: sync?.last_action || sync?.lastAction || '',
    lastError: sync?.last_error || sync?.lastError || '',
    retryable: Boolean(sync?.retryable),
    retryAfter: sync?.retry_after || sync?.retryAfter || '',
    statusCapable: Boolean(sync?.status_capable || sync?.statusCapable),
    divergent: Boolean(sync?.divergent),
    operatorReviewRequired: Boolean(
      sync?.operator_review_required || sync?.operatorReviewRequired
    ),
    capabilities: sync?.capabilities || {},
    readiness: normalizeReadiness(sync?.readiness),
    actions,
    attemptCounts,
    attemptTotal: Object.values(attemptCounts).reduce(
      (sum, count) => sum + Number(count || 0),
      0
    ),
    lastAttempt: normalizeAttempt(sync?.last_attempt || sync?.lastAttempt),
    attemptHistory,
    submittedAt: sync?.submitted_at || sync?.submittedAt || '',
    remoteStatusCheckedAt:
      sync?.remote_status_checked_at || sync?.remoteStatusCheckedAt || '',
    nextStatusCheckAt:
      sync?.next_status_check_at || sync?.nextStatusCheckAt || '',
    updatedAt: sync?.updated_at || sync?.updatedAt || '',
  };
};

export const normalizeSyncsResponse = response => ({
  syncs: Array.isArray(response?.payload)
    ? response.payload.map(item => normalizeSync(item))
    : [],
  meta: response?.meta || {
    count: 0,
    total_count: 0,
    page: 1,
    per_page: 50,
    total_pages: 0,
  },
  summary: response?.summary || {},
});

export const attemptCountEntries = sync =>
  Object.entries(sync?.attemptCounts || {})
    .map(([status, count]) => ({ status, count: Number(count || 0) }))
    .filter(entry => entry.count > 0);

export const actionForSync = (sync, action) =>
  (sync?.actions || []).find(item => item.key === action) || null;

export const canOperateSync = (sync, action = '') => {
  if (action) {
    const operation = actionForSync(sync, action);
    if (operation) return operation.enabled;
  }

  return (
    Boolean(sync?.statusCapable) &&
    !TERMINAL_PORTAL_SYNC_STATUSES.includes(sync?.status)
  );
};

export const disabledReasonForSync = (sync, action) =>
  actionForSync(sync, action)?.disabledReason || '';

export const operationPayload = ({
  action,
  id,
  reason,
  nonce = Date.now(),
}) => ({
  reason,
  idempotency_key: `dashboard:portal-sync:${action}:${id}:${nonce}`,
});
